import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import Pagination from '../navigation/Pagination';
import Modal from '../overlay/Modal';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import styled from 'styled-components';
import {
  AuditEvent, AuditEventDetail, AuditEventPage, AuditFilters, RevertConflict,
} from '../../models/audit';
import { getAuditEvent, getAuditEvents, revertAuditEvent } from '../../requests/audit.request';
import ChangeDiff, { formatValue } from './ChangeDiff';

const ACTION_LABELS: Record<string, string> = {
  'auth.login': 'Signed in',
  'auth.logout': 'Signed out',
  'auth.login_failed': 'Failed sign-in',
  'audit.revert': 'Reverted a change',
  'css.update': 'Custom CSS',
  'widget.update': 'Widget settings',
  'dashboard.update': 'Dashboard features',
  'billing.update': 'Billing plan',
  'billing.reset': 'Billing reset',
  'billing.reactivate': 'Billing reactivated',
  'billing.dev': 'Dev billing plan',
  'coupon.update': 'Coupon',
  'onboarding.update': 'Onboarding steps',
  'size_mapping.update': 'Size mapping',
  'size_conversion.update': 'Size conversion',
  'category.create': 'New category',
  'shop.create': 'New shop',
  'advanced.customcss.update': 'Advanced custom CSS',
  'advanced.customjs.update': 'Custom JavaScript',
  'advanced.widgetcss.update': 'Advanced widget CSS',
  'advanced.widgetfeatures.update': 'Widget features JSON',
  'translations.default.update': 'Default translations',
  'translations.widget.update': 'Widget translations',
  'translations.category.update': 'Category translations',
  'shopify.webhook.create': 'Shopify webhook added',
  'shopify.webhook.update': 'Shopify webhook address changed',
  'shopify.webhook.delete': 'Shopify webhook deleted',
  'shopify.webhook.fix': 'Shopify webhooks fixed',
  'shopify.graphql.mutation': 'Shopify GraphQL mutation',
};

// s-option drops an empty value (the option's label becomes its value), so
// "All activity" uses a placeholder value that maps to no action filter.
const ALL_ACTIVITY = 'all';
const ACTION_FILTERS = [
  { label: 'All activity', value: ALL_ACTIVITY },
  { label: 'Sign-ins', value: 'auth.' },
  { label: 'Reverts', value: 'audit.revert' },
  { label: 'Billing', value: 'billing.' },
  { label: 'Coupons', value: 'coupon.' },
  { label: 'Translations', value: 'translations.' },
  { label: 'Advanced settings', value: 'advanced.' },
  { label: 'Size conversion', value: 'size_conversion.' },
  { label: 'Size mapping', value: 'size_mapping.' },
  { label: 'New shops', value: 'shop.' },
  { label: 'Shopify', value: 'shopify.' },
];

export const actionLabel = (action: string) => ACTION_LABELS[action] || action;

// Labels for keys in an event's `detail`, which records what changed outside our databases (e.g. in Shopify).
const DETAIL_LABELS: Record<string, string> = {
  topic: 'Topic',
  uri: 'Address',
  previous_uri: 'Previous address',
  webhook_id: 'Webhook ID',
  webhooks: 'Webhooks',
  query: 'Query',
  variables: 'Variables',
  operation_name: 'Operation',
};

type WebhookDetail = { topic?: unknown; uri?: unknown };
const webhookText = ({ topic, uri }: WebhookDetail) => `${topic} → ${uri}`;

const formatDetail = (key: string, value: unknown) => {
  if (key === 'webhooks' && Array.isArray(value)) return value.map(webhookText).join('\n');
  return formatValue(value);
};

// One line for the collapsed row, so Shopify events show what they touched without expanding.
const eventSummary = ({ detail }: AuditEvent) => {
  if (detail?.topic && detail?.uri) return webhookText(detail);
  if (Array.isArray(detail?.webhooks)) {
    const count = detail.webhooks.length;
    return `${count} ${count === 1 ? 'webhook' : 'webhooks'}`;
  }
  const { query, operation_name: operationName } = detail ?? {};
  if (typeof operationName === 'string' && operationName) return operationName;
  if (typeof query === 'string') return query.trim().split('\n')[0].slice(0, 120);
  return null;
};

const STATUS_BADGES: Record<AuditEvent['status'], { tone: 'success' | 'critical' | 'caution'; label: string }> = {
  success: { tone: 'success', label: 'Success' },
  failed: { tone: 'critical', label: 'Failed' },
  pending: { tone: 'caution', label: 'Incomplete' },
};

type RevertError = { message: string; conflicts?: RevertConflict[] };

type AuditTimelineProps = {
  shopId?: string;
  showFilters?: boolean;
};

const AuditTimeline = ({ shopId, showFilters = false }: AuditTimelineProps) => {
  const [draft, setDraft] = useState<AuditFilters>({});
  const [filters, setFilters] = useState<AuditFilters>({});
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AuditEventPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [details, setDetails] = useState<Record<number, AuditEventDetail>>({});
  const [confirm, setConfirm] = useState<AuditEvent | null>(null);
  const [reverting, setReverting] = useState(false);
  const [revertError, setRevertError] = useState<RevertError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(await getAuditEvents({ ...filters, shop_id: shopId, page }));
    } catch (err: any) {
      setLoadError(err?.response?.data?.detail || 'The audit log could not be loaded. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [filters, shopId, page]);

  useEffect(() => {
    load();
  }, [load]);

  const loadDetail = async (id: number) => {
    const detail = await getAuditEvent(id);
    setDetails((current) => ({ ...current, [id]: detail }));
  };

  const toggle = async (event: AuditEvent) => {
    const open = !expanded[event.id];
    setExpanded((current) => ({ ...current, [event.id]: open }));
    if (open && !details[event.id]) {
      try {
        await loadDetail(event.id);
      } catch {
        setExpanded((current) => ({ ...current, [event.id]: false }));
        setLoadError('The change details could not be loaded. Please retry.');
      }
    }
  };

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setPage(1);
    setFilters(draft);
  };

  const revert = async () => {
    if (!confirm) return;
    setReverting(true);
    setRevertError(null);
    setNotice(null);
    try {
      const result = await revertAuditEvent(confirm.id);
      setDetails((current) => ({ ...current, [result.event.id]: result.event, [result.revert.id]: result.revert }));
      setNotice(`Reverted “${actionLabel(confirm.action)}” from ${new Date(confirm.created_at).toLocaleString()}.`);
      setConfirm(null);
      await load();
    } catch (err: any) {
      const body = err?.response?.data || {};
      setRevertError({
        message: body.message || body.detail || 'The change could not be reverted. Nothing was changed.',
        conflicts: body.conflicts,
      });
      setConfirm(null);
      await load();
    } finally {
      setReverting(false);
    }
  };

  const events = data?.results || [];
  const pageCount = data ? Math.max(Math.ceil(data.count / data.page_size), 1) : 1;

  return (
    <Wrapper>
      {showFilters && (
        <Filters onSubmit={applyFilters} aria-label='Filter audit log'>
          <s-text-field label='User' value={draft.actor || ''} autocomplete='off' placeholder='name@easysize.me'
            onInput={(event) => setDraft({ ...draft, actor: event.currentTarget.value })} />
          <s-select label='Activity' value={draft.action || ALL_ACTIVITY}
            onChange={(event) => {
              const { value } = event.currentTarget;
              setDraft({ ...draft, action: value === ALL_ACTIVITY ? '' : value });
            }}>
            {ACTION_FILTERS.map(({ label, value }) => <s-option key={value} value={value}>{label}</s-option>)}
          </s-select>
          <s-date-field label='From' value={draft.since || ''}
            onChange={(event) => setDraft({ ...draft, since: event.currentTarget.value })} />
          <s-date-field label='To' value={draft.until || ''}
            onChange={(event) => setDraft({ ...draft, until: event.currentTarget.value })} />
          <FilterActions>
            <s-button type='submit' variant='primary'>Apply</s-button>
          </FilterActions>
        </Filters>
      )}

      <Toolbar>
        <span>{data ? `${data.count} ${data.count === 1 ? 'event' : 'events'}` : ''}</span>
        <s-button icon='refresh' onClick={load} disabled={loading}>Refresh</s-button>
      </Toolbar>

      {notice && <s-banner tone='success' dismissible onDismiss={() => setNotice(null)}><p>{notice}</p></s-banner>}
      {revertError && (
        <s-banner tone='critical' heading='Nothing was reverted' dismissible onDismiss={() => setRevertError(null)}>
          <p>{revertError.message}</p>
          {revertError.conflicts && (
            <ul>
              {revertError.conflicts.map((conflict) => (
                <li key={`${conflict.model}-${JSON.stringify(conflict.pk)}`}>
                  {conflict.model} row {JSON.stringify(conflict.pk)}: {conflict.current ? 'changed since' : 'no longer exists'}
                </li>
              ))}
            </ul>
          )}
        </s-banner>
      )}
      {loadError && <s-banner tone='critical' dismissible onDismiss={() => setLoadError(null)}><p>{loadError}</p></s-banner>}

      {loading && !data ? (
        <Centered><s-spinner accessibilityLabel='Loading audit log' size='large' /></Centered>
      ) : events.length === 0 ? (
        <Empty>No activity recorded yet.</Empty>
      ) : (
        <List>
          {events.map((event) => {
            const badge = STATUS_BADGES[event.status];
            const detail = details[event.id];
            const isOpen = !!expanded[event.id];
            const summary = eventSummary(event);
            const detailEntries = Object.entries(event.detail ?? {});
            return (
              <Item key={event.id}>
                <Summary>
                  <ExpandButton type='button' aria-expanded={isOpen} aria-controls={`audit-event-${event.id}`}
                    onClick={() => toggle(event)}>
                    {isOpen ? <MdExpandLess aria-hidden='true' /> : <MdExpandMore aria-hidden='true' />}
                    <ActionName>{actionLabel(event.action)}</ActionName>
                  </ExpandButton>
                  <Meta>
                    <time dateTime={event.created_at}>{new Date(event.created_at).toLocaleString()}</time>
                    <span>{event.actor_email || 'unknown user'}</span>
                    {!shopId && event.shop_id && <span>shop {event.shop_id}</span>}
                    {summary && <SummaryText>{summary}</SummaryText>}
                    {event.change_count > 0 && <span>{event.change_count} {event.change_count === 1 ? 'row' : 'rows'}</span>}
                  </Meta>
                  <Tags>
                    <s-badge tone={badge.tone}>{badge.label}</s-badge>
                    {event.reverted_by && <s-badge>{`Reverted by #${event.reverted_by}`}</s-badge>}
                    {event.reverts && <s-badge tone='info'>{`Revert of #${event.reverts}`}</s-badge>}
                    <EventId>#{event.id}</EventId>
                  </Tags>
                  {event.can_revert && (
                    <s-button onClick={() => setConfirm(event)}>Revert</s-button>
                  )}
                </Summary>
                {isOpen && (
                  <Details id={`audit-event-${event.id}`}>
                    {event.error && <ErrorText>{event.error}</ErrorText>}
                    {detailEntries.length > 0 && (
                      <DetailList>
                        {detailEntries.map(([key, value]) => (
                          <React.Fragment key={key}>
                            <dt>{DETAIL_LABELS[key] || key}</dt>
                            <dd>{formatDetail(key, value)}</dd>
                          </React.Fragment>
                        ))}
                      </DetailList>
                    )}
                    {!detail ? <s-spinner accessibilityLabel='Loading changes' /> : (
                      detail.changes.map((change) => <ChangeDiff key={change.id} change={change} />)
                    )}
                    {detail && !detail.changes.length && !detailEntries.length && !event.error && (
                      <Muted>No data changed: the saved values were the same as before.</Muted>
                    )}
                  </Details>
                )}
              </Item>
            );
          })}
        </List>
      )}

      {pageCount > 1 && (
        <PaginationRow>
          <Pagination
            label={`Page ${page} of ${pageCount}`}
            hasPrevious={page > 1}
            onPrevious={() => setPage(page - 1)}
            hasNext={page < pageCount}
            onNext={() => setPage(page + 1)}
          />
        </PaginationRow>
      )}

      <Modal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        heading='Revert this change?'
        primaryAction={{ content: 'Revert', destructive: true, loading: reverting, onAction: revert }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setConfirm(null) }]}
      >
        <p>
          {confirm && `“${actionLabel(confirm.action)}” by ${confirm.actor_email || 'unknown user'} on ${new Date(confirm.created_at).toLocaleString()} `}
          will be undone on the live configuration. If any affected row changed after this event, nothing is reverted.
        </p>
      </Modal>
    </Wrapper>
  );
};

export default AuditTimeline;

const Wrapper = styled.div`
  display: grid;
  gap: 12px;
  min-width: 0;
`;

const Filters = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  align-items: end;
`;

const FilterActions = styled.div`
  display: flex;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #6d7175;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const Empty = styled.p`
  color: #6d7175;
  padding: 24px 0;
`;

const List = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
`;

const Item = styled.li`
  border: 1px solid #e1e3e5;
  border-radius: 8px;
  background: #fff;
  min-width: 0;
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  padding: 10px 12px;
`;

const ExpandButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 0;
  padding: 2px 4px;
  font: inherit;
  cursor: pointer;
  border-radius: 4px;
  svg { flex-shrink: 0; font-size: 20px; }
  &:focus-visible { outline: 2px solid #2c6ecb; outline-offset: 2px; }
`;

const ActionName = styled.span`
  font-weight: 600;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  color: #6d7175;
  font-size: 13px;
`;

const SummaryText = styled.span`
  overflow-wrap: anywhere;
  font-family: monospace;
  font-size: 12px;
  color: #303030;
`;

const DetailList = styled.dl`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 4px 16px;
  margin: 0;
  font-size: 13px;
  dt { color: #6d7175; }
  dd { margin: 0; font-family: monospace; font-size: 12px; white-space: pre-wrap; overflow-wrap: anywhere; }
`;

const Muted = styled.p`
  margin: 0;
  color: #6d7175;
  font-size: 13px;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-left: auto;
`;

const EventId = styled.span`
  color: #8c9196;
  font-size: 12px;
`;

const Details = styled.div`
  display: grid;
  gap: 8px;
  padding: 0 12px 12px;
`;

const ErrorText = styled.p`
  color: #d72c0d;
  font-family: monospace;
  font-size: 12px;
  white-space: pre-wrap;
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: center;
`;
